import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, Copy, Crown, Pencil, LineChart } from "lucide-react";

const ROLE_META = {
  owner: { icon: Crown, label: "Owner", tint: "#0B5CFF" },
  editor: { icon: Pencil, label: "Editor", tint: "#0B0D17" },
  analyst: { icon: LineChart, label: "Analyst", tint: "#1E8E3E" },
};

export default function Team() {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [sending, setSending] = useState(false);

  const load = async () => {
    const [m, i] = await Promise.all([api.get("/team/members"), api.get("/team/invites")]);
    setMembers(m.data); setInvites(i.data);
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/team/invite", { email, role });
      navigator.clipboard.writeText(data.invite_url);
      toast.success("Invite created — link copied to clipboard");
      setEmail("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSending(false); }
  };

  const setRoleOf = async (id, newRole) => {
    try { await api.patch(`/team/members/${id}`, { role: newRole }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const removeMember = async (id) => {
    if (!window.confirm("Remove from workspace? They keep their own account.")) return;
    try { await api.delete(`/team/members/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const cancelInvite = async (id) => {
    try { await api.delete(`/team/invites/${id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-6 vp-fade-up">
      <div>
        <div className="vp-label">Team</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight tracking-tight mt-1">Your workspace.</h1>
        <p className="text-[#4A4D57] mt-2 text-sm">Invite editors and analysts to collaborate on webinars.</p>
      </div>

      {/* Invite form */}
      <form onSubmit={invite} className="vp-card p-5 flex flex-col sm:flex-row gap-3">
        <input type="email" required placeholder="teammate@brand.com" className="vp-input flex-1"
          value={email} onChange={(e) => setEmail(e.target.value)} data-testid="team-invite-email" />
        <select className="vp-input max-w-[160px]" value={role} onChange={(e) => setRole(e.target.value)} data-testid="team-invite-role">
          <option value="editor">Editor</option>
          <option value="analyst">Analyst</option>
          <option value="owner">Owner</option>
        </select>
        <button disabled={sending} type="submit" data-testid="team-invite-btn"
          className="vp-btn-primary inline-flex items-center gap-2 justify-center">
          <UserPlus className="w-4 h-4" /> {sending ? "Sending…" : "Invite"}
        </button>
      </form>

      {/* Pending invites */}
      {invites.length > 0 && (
        <div className="vp-card p-5">
          <div className="vp-label mb-3">Pending invites · {invites.length}</div>
          <ul className="divide-y divide-[#E4E6EC]">
            {invites.map((i) => (
              <li key={i.id} className="flex items-center gap-3 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{i.email}</div>
                  <div className="text-xs text-[#747680]">{i.role} · sent {new Date(i.created_at).toLocaleString("en-IN")}</div>
                </div>
                <button onClick={() => cancelInvite(i.id)} className="vp-btn-ghost text-xs py-1.5 px-3 inline-flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> Cancel
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Members */}
      <div className="vp-card p-2 sm:p-4 overflow-x-auto">
        <table className="vp-table min-w-[560px]">
          <thead><tr><th>Member</th><th>Email</th><th>Role</th><th></th></tr></thead>
          <tbody>
            {members.map((m) => {
              const meta = ROLE_META[m.role] || ROLE_META.editor;
              return (
                <tr key={m.id}>
                  <td className="font-semibold">{m.name || "—"}</td>
                  <td className="text-[#4A4D57]">{m.email}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="vp-chip vp-chip-accent inline-flex items-center gap-1">
                        <meta.icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {meta.label}
                      </span>
                      <select value={m.role} onChange={(e) => setRoleOf(m.id, e.target.value)}
                        className="vp-input text-xs py-1 px-2 max-w-[110px]" data-testid={`role-${m.id}`}>
                        <option value="owner">Owner</option>
                        <option value="editor">Editor</option>
                        <option value="analyst">Analyst</option>
                      </select>
                    </div>
                  </td>
                  <td className="text-right">
                    {m.role !== "owner" && (
                      <button onClick={() => removeMember(m.id)} className="vp-btn-ghost text-xs py-1.5 px-3 inline-flex items-center gap-1">
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
